import { useLanguage } from '../contexts/LanguageContext';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useEffect, useRef } from 'react';

export default function AuditingService() {
    const { t } = useLanguage();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const descriptionRef = useRef<HTMLParagraphElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Animate title
                        if (entry.target === titleRef.current) {
                            entry.target.classList.add('animate-fade-in-up');
                        }
                        // Animate description
                        if (entry.target === descriptionRef.current) {
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 200);
                        }
                        // Animate cards container
                        if (entry.target === cardsRef.current) {
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 400);
                        }
                        // Animate individual cards with staggered delay
                        if (cardRefs.current.includes(entry.target as HTMLDivElement)) {
                            const index = cardRefs.current.indexOf(entry.target as HTMLDivElement);
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 600 + (index * 150)); // 600ms base delay + 150ms per card
                        }
                    }
                });
            },
            { threshold: 0.1 }
        );

        // Observe all elements
        if (titleRef.current) observer.observe(titleRef.current);
        if (descriptionRef.current) observer.observe(descriptionRef.current);
        if (cardsRef.current) observer.observe(cardsRef.current);
        cardRefs.current.forEach((card) => {
            if (card) observer.observe(card);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} id="auditingService" className="min-h-screen md:h-[100vh] bg-transparent relative overflow-hidden flex items-center py-8 md:py-0">
            {/* Corner decorations */}
            <div className="absolute top-[22px] -left-[10px] sm:-left-[30px] md:-left-[80px] md:h-[200px] h-[50px] sm:h-[70px] rotate-[79deg] hidden sm:block">
                <img src='/images/corner-decorations.png' alt='' className='w-full h-full' />
            </div> 
            <div className="absolute bottom-[0] right-[-10px] sm:right-[-30px] md:right-[-80px] md:h-[180px] h-[50px] sm:h-[70px] rotate-[-100deg] hidden sm:block">
                <img src='/images/corner-decorations.png' alt='' className='w-full h-full' />
            </div>
            
            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center mb-16">
                    <h1 
                        ref={titleRef}
                        className="text-4xl md:text-5xl font-bold text-[#244473] mb-6 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:text-blue-600 cursor-pointer"
                    >
                        {t("services.auditing.title")}
                    </h1>
                    <p 
                        ref={descriptionRef}
                        className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto opacity-0 translate-y-8 transition-all duration-700 ease-out hover:text-blue-600 cursor-pointer"
                    >
                        {t("services.auditing.description")}
                    </p>
                </div>

                <div 
                    ref={cardsRef}
                    className="grid md:grid-cols-3 grid-cols-1 gap-8 max-w-6xl mx-auto opacity-0 translate-y-8 transition-all duration-700 ease-out"
                >
                    <div 
                        ref={(el) => { cardRefs.current[0] = el; }}
                        className="bg-white rounded-xl shadow-lg hover:shadow-2xl hover:shadow-blue-500/20 p-8 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:bg-blue-50 group cursor-pointer border border-gray-100"
                    >
                        <div className="text-center">
                            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-200 group-hover:scale-110 transition-all duration-300">
                                <AssessmentIcon 
                                    sx={{ fontSize: 40 }} 
                                    className="text-blue-600 group-hover:text-blue-700 group-hover:rotate-12 transition-all duration-300" 
                                />
                            </div>
                            <h3 className="text-xl font-bold text-[#244473] mb-4 group-hover:text-blue-600 transition-colors duration-300">
                                {t("services.auditing.cards.card1.title")}
                            </h3>
                            <p className="text-gray-600 group-hover:text-blue-700 transition-colors duration-300 leading-relaxed">
                                {t("services.auditing.cards.card1.description")}
                            </p>
                        </div>
                    </div>

                    <div 
                        ref={(el) => { cardRefs.current[1] = el; }}
                        className="bg-white rounded-xl shadow-lg hover:shadow-2xl hover:shadow-blue-500/20 p-8 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:bg-blue-50 group cursor-pointer border border-gray-100"
                    >
                        <div className="text-center">
                            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-200 group-hover:scale-110 transition-all duration-300">
                                <VerifiedUserIcon 
                                    sx={{ fontSize: 40 }} 
                                    className="text-blue-600 group-hover:text-blue-700 group-hover:rotate-12 transition-all duration-300" 
                                />
                            </div>
                            <h3 className="text-xl font-bold text-[#244473] mb-4 group-hover:text-blue-600 transition-colors duration-300">
                                {t("services.auditing.cards.card2.title")}
                            </h3>
                            <p className="text-gray-600 group-hover:text-blue-700 transition-colors duration-300 leading-relaxed">
                                {t("services.auditing.cards.card2.description")}
                            </p>
                        </div>
                    </div>

                    <div 
                        ref={(el) => { cardRefs.current[2] = el; }}
                        className="bg-white rounded-xl shadow-lg hover:shadow-2xl hover:shadow-blue-500/20 p-8 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:bg-blue-50 group cursor-pointer border border-gray-100"
                    >
                        <div className="text-center">
                            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-200 group-hover:scale-110 transition-all duration-300">
                                <ReceiptIcon 
                                    sx={{ fontSize: 40 }} 
                                    className="text-blue-600 group-hover:text-blue-700 group-hover:rotate-12 transition-all duration-300" 
                                />
                            </div>
                            <h3 className="text-xl font-bold text-[#244473] mb-4 group-hover:text-blue-600 transition-colors duration-300">
                                {t("services.auditing.cards.card3.title")}
                            </h3>
                            <p className="text-gray-600 group-hover:text-blue-700 transition-colors duration-300 leading-relaxed">
                                {t("services.auditing.cards.card3.description")}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}